/**
 * A sortable column heading.
 *
 * One component, used by every table, so the affordance, the indicator and
 * the accessible name are identical everywhere. A column that holds row
 * actions uses a plain `<th>` instead: there is nothing to order it by.
 */

import { ChevronDown, ChevronUp, ChevronsUpDown } from 'lucide-react';

import { sortAriaLabel, type SortState } from './tableSort';

interface Props<Column extends string> {
  /** The column this heading orders by. */
  column: Column;
  /** The heading text, and what the accessible name is built from. */
  label: string;
  sort: SortState<Column>;
  onSort: (column: Column) => void;
  /** Extra classes for the `<th>`, for the few columns that need a width. */
  className?: string;
  /** Rendered after the label, for a unit or a timezone note. */
  children?: React.ReactNode;
}

export function SortableHeader<Column extends string>({
  column,
  label,
  sort,
  onSort,
  className,
  children,
}: Props<Column>) {
  const active = sort.column === column;
  const ariaSort = active ? (sort.direction === 'asc' ? 'ascending' : 'descending') : 'none';

  return (
    <th
      scope="col"
      className={className ? `sortable ${className}` : 'sortable'}
      aria-sort={ariaSort}
    >
      <button
        type="button"
        className="th-sort"
        aria-label={sortAriaLabel(label, active, sort.direction)}
        onClick={() => onSort(column)}
      >
        <span className="th-inner">
          {label}
          {children}
          <span className={active ? 'sort-icon is-active' : 'sort-icon'} aria-hidden="true">
            {!active ? (
              <ChevronsUpDown />
            ) : sort.direction === 'asc' ? (
              <ChevronUp />
            ) : (
              <ChevronDown />
            )}
          </span>
        </span>
      </button>
    </th>
  );
}
